import React, { isValidElement, useCallback, useEffect } from 'react';

import { connect, useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { IconButton } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { InventoryListingOverlayActions } from 'reaxl-listing';

import compareListingsHandler from '@/utilities/compareListingsHandler';
import { saveVDPListing } from '@/utilities/saveListing';

import {
    authDuck,
    compareListingsDuck,
    savedInventoryDuck,
    userDuck,
    userPreferencesDuck,
} from '@/ducks';

import {
    vdpActiveInteractionDuck,
    vdpResultsDuck,
} from '@/ducks/vdp';

import ShareContentContainer from '@/containers/ShareContentContainer';

export function SaveAndShareContainer({
    listing,
    listingIdsToCompare,
    isSaved,
    className,
    isLoggedIn,
    children,
}) {

    const {
        compare: [enableCompare, { showSpotlight }],
        personalization: [isSaveEnabled, { listings_saved: authenticatedSavesOnly }],
        share: [isShareEnabled],
    } = useFeatures([
        'compare',
        'personalization',
        'share',
    ]);

    const dispatch = useDispatch();

    // retrieve saved inventory ids and set them in the inventory reducer
    const initializeUserInfo = useCallback(() => {
        dispatch(userDuck.creators.hydrateUserInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    const { brands, isBrand } = useBrand();
    const isAtc = isBrand(brands.ATC_BRAND);
    const isKbbBranded = isBrand(brands.KBB_BRAND);
    const isSaveListingEnabled = isSaveEnabled || authenticatedSavesOnly;
    const isCompared = listingIdsToCompare.includes(listing.id);

    const isOverlay = isValidElement(children);

    listing.birfParams = {
        co_txt_url: 'save',
        c: listing?.owner?.id,
        v: listing.id,
        par: 'v_lstg',
    };

    useEffect(() => {
        // Only call this function on KBB
        if (isKbbBranded) {
            initializeUserInfo(); // Hydrate saved inventory
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const setActiveInteraction = useCallback((keys) => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys(keys));
    }, [dispatch]);

    const addListingToCompare = useCallback((listingId) => {
        dispatch(compareListingsDuck.creators.addToCompare(listingId));
    }, [dispatch]);

    const fetchCompareListings = useCallback((comparedListingIds = [], numRecords = 3, loadELot = false) => {
        dispatch(compareListingsDuck.creators.loadListingsData(comparedListingIds, numRecords, loadELot));
    }, [dispatch]);

    // compare currently saved listings and the current vdp listing if it is not already saved
    const handleCompare = useCallback((event) => {
        dispatch(userPreferencesDuck.creators.setCompareToggle(true));
        dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));

        compareListingsHandler({
            addToActiveResults: !isCompared && addListingToCompare,
            event,
            isAddToCompare: true,
            fetchCompareListings,
            listing,
            listingIds: listingIdsToCompare,
            setActiveInteraction,
            showCompareModal: false,
            showSpotlight,
        });
    }, [addListingToCompare, dispatch, fetchCompareListings, isCompared, listing, listingIdsToCompare, setActiveInteraction, showSpotlight]);

    const handleSave = useCallback((event) => {
        dispatch(saveVDPListing({
            event,
            listing,
            wasSaved: isSaved,
            sendClick,
        }));
    }, [dispatch, listing, isSaved]);

    const enableCompareButton = enableCompare && (listingIdsToCompare?.length !== 5 || listingIdsToCompare?.includes(listing?.id));

    if (isOverlay) {
        return (
            <div
                data-cmp="saveAndShareOverlay"
                className="display-flex flex-column justify-content-center"
            >
                <div
                    className="overlay-actions-container"
                    style={{ height: '0', zIndex: '1' }}
                >
                    <InventoryListingOverlayActions
                        listing={listing}
                        onSaveToggle={handleSave}
                        isSaved={isSaved}
                        additionalCta={isShareEnabled && (
                            <ShareContentContainer
                                key="share-glyph"
                                glyphColor={isAtc ? 'accent' : 'default'}
                                uiContext="glyph"
                                className="display-flex margin-right-4"
                            />
                        )}
                    />
                </div>
                {children}
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'padding-horizontal-2 padding-horizontal-sm-0 display-flex gap-2 justify-content-end align-items-center margin-vertical-2 text-bold',
                className
            )}
            style={{ minHeight: '40px' }}
        >
            {enableCompareButton && (
                <IconButton
                    disabled={isCompared}
                    glyph={isCompared ? 'success' : 'open'}
                    label={isCompared ? 'Added to Compare' : 'Compare'}
                    size="small"
                    monochrome={!isAtc}
                    onClick={handleCompare}
                    data-cmp="addToCommpareBtn"
                />
            )}

            {isShareEnabled && (
                <ShareContentContainer
                    glyphColor={isAtc ? 'accent' : 'default'}
                    uiContext="inline"
                />
            )}
            {isSaveListingEnabled && (
                <IconButton
                    onClick={handleSave}
                    glyph={isSaved ? 'heart' : 'heart-o'}
                    label={isSaved ? 'Saved' : 'Save'}
                    size="small"
                    monochrome={!isAtc}
                    data-cmp="saveButton"
                />
            )}
        </div>
    );
}

const mapStateToProps = (state) => {
    const isLoggedIn = authDuck.selectors.isLoggedIn(state);
    const listing = vdpResultsDuck.selectors.getVdpActiveInventory(state);
    const listingIdsToCompare = compareListingsDuck.selectors.getActiveListingIds(state) || [];
    const savedListingIds = savedInventoryDuck.selectors.getSavedInventoryIds(state) || [];

    return {
        listing,
        listingIdsToCompare,
        isSaved: savedListingIds.includes(listing.id),
        savedListingIds,
        isLoggedIn,
    };
};
SaveAndShareContainer.propTypes = {
    /**
     * listing data
     */
    listing: PropTypes.object,
    /**
     * Array of listing IDs that are currently being compared
     */
    listingIdsToCompare: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    /**
     * Boolean indicating if the current listing is saved
     */
    isSaved: PropTypes.bool,
    /**
     * Custom className for the container
     */
    className: PropTypes.string,
    /**
     * Boolean indicating if user is logged in
     */
    isLoggedIn: PropTypes.bool,
    /**
     * Child elements to be rendered
     */
    children: PropTypes.node,
};

export default connect(mapStateToProps)(SaveAndShareContainer);
